import { OptionTypeBase } from '@sprint/sprint-react-components';
import React, { ReactNode, useState } from 'react';
import { ActionMeta, FormatOptionLabelMeta, GroupBase, Options } from 'react-select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { SelectComponents } from 'react-select/dist/declarations/src/components';

interface Props {
    id: string;
    value: OptionTypeBase | undefined | null;
    onChange: (selected: Options<OptionTypeBase>, action: ActionMeta<any>) => void;
    loadOptions: (filter: string, loadedOptions: any, page: any) => any;
    onFilter?: (filter: string) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
    placeholder?: string;
    isMulti?: boolean;
    onSubmit?: () => void;
    formatOptionLabel?: ((data: any, formatOptionLabelMeta: FormatOptionLabelMeta<any>) => ReactNode) | undefined;
    components?: Partial<SelectComponents<any, boolean, GroupBase<any>>> | undefined;
    isClearable?: boolean;
    controlledInputValue?: string;
}

const PillTextField = React.forwardRef((props: Props, ref: React.ForwardedRef<any>) => {
    const [internalInputValue, setInternalInputValue] = useState<string>(props.value?.label ?? '');
    const [isInvalid, setIsInvalid] = useState<boolean>(false);

    // Use controlled input value if provided, otherwise use internal state
    const inputValue = props.controlledInputValue !== undefined ? props.controlledInputValue : internalInputValue;

    return (
        <AsyncPaginate
            id={props.id}
            isMulti={props.isMulti ? true : false}
            closeMenuOnSelect={true}
            inputValue={inputValue}
            className={`form-dropdown ${isInvalid ? 'is-invalid' : ''}`}
            placeholder={props.placeholder ?? 'Please select'}
            value={props.value}
            openMenuOnFocus={false}
            onBlur={(event) => {
                if (props.onBlur) props.onBlur(event);
            }}
            onChange={(selected, action) => {
                props.onChange(selected, action);
                setIsInvalid(false);
                // Scroll to bottom of value container on input select
                if (action.action === 'select-option') {
                    setTimeout(() => {
                        const input = document.querySelector(`#${props.id} [class$="-ValueContainer"]`);
                        input?.scroll(0, input.scrollHeight);
                    }, 50);
                }
            }}
            onInputChange={(filter, action) => {
                if (action.action === 'input-change') {
                    setInternalInputValue(filter);
                    if (props.onFilter) props.onFilter(filter);
                }
                if (action.action === 'menu-close') {
                    setInternalInputValue('');
                    setIsInvalid(false);
                }
            }}
            onKeyDown={(event) => {
                if (props.onSubmit && inputValue.length === 0 && event.key === 'Enter') {
                    props.onSubmit();
                }
            }}
            additional={{
                page: 1,
            }}
            loadOptions={props.loadOptions}
            theme={(theme) => ({
                ...theme,
                colors: {
                    ...theme.colors,
                    primary: '#0099cc',
                    primary25: '#f6f6f6',
                    primary50: '#f6f6f6',
                    primary75: '#f6f6f6',
                },
            })}
            styles={{
                dropdownIndicator: () => ({
                    display: 'none',
                }),
                indicatorSeparator: () => ({
                    display: 'none',
                }),
                loadingIndicator: () => ({
                    display: 'none',
                }),
                menu: () => ({
                    display: 'none',
                }),
                menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
                valueContainer: (provided) => ({
                    ...provided,
                    minHeight: '32px',
                    maxHeight: '139px',
                    overflowY: 'auto',
                }),
                container: (provided) => ({
                    ...provided,
                    minHeight: '34px',
                    borderRadius: '3px',
                    border: isInvalid ? '1px solid red' : '1px solid #e7e7e7',
                    ':focus': {
                        ...provided[':focus'],
                        border: '1px solid #0099cc',
                    },
                }),
                control: (provided, { isDisabled }) => ({
                    ...provided,
                    background: isDisabled ? '#e9ecef' : 'none',
                    minHeight: 20,
                    lineHeight: 'normal',
                    border: 'none',
                    borderRadius: '3px',
                    fontSize: '14px',
                    ':focus': {
                        ...provided[':focus'],
                        border: 'none',
                    },
                }),
                singleValue: (provided) => ({
                    ...provided,
                    top: '55%',
                    color: '#666979',
                }),
                input: (provided) => ({
                    ...provided,
                    margin: '0 2px 0 2px',
                    padding: 0,
                    input: {
                        gridArea: '1 / 2 / 4 / 4 !important',
                    },
                }),
                option: (provided, { isSelected }) => ({
                    ...provided,
                    color: isSelected ? 'white' : '#333',
                }),
            }}
            formatOptionLabel={props.formatOptionLabel}
            components={props.components}
            isClearable={props.isClearable}
            selectRef={ref}
        />
    );
});

export default PillTextField;
